import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, Button, TextField, InputAdornment, Table, TableHead, TableBody,
  TableRow, TableCell, TablePagination, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Tabs, Tab, Typography, Alert,
  Tooltip, Chip, Stack, Divider, Avatar
} from '@mui/material';
import AddIcon        from '@mui/icons-material/AddRounded';
import SearchIcon     from '@mui/icons-material/SearchRounded';
import EditIcon       from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import SendIcon       from '@mui/icons-material/SendRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import {
  fetchTasks, fetchMyTasks, createTask, updateTask,
  updateTaskStatus, addComment, requestApproval, fetchTask
} from '../../app/slices/tasksSlice';
import { fetchUsers }       from '../../app/slices/usersSlice';
import { fetchDepartments } from '../../app/slices/departmentsSlice';
import { PageHeader }       from '../../components/common/PageHeader';
import { StatusChip, ApprovalChip } from '../../components/common/StatusChip';
import { LoadingSpinner }   from '../../components/common/LoadingSpinner';
import { EmptyState }       from '../../components/common/EmptyState';
import { formatDate, formatDateTime, isOverdue, getInitials } from '../../utils/helpers';
import { STATUS_OPTIONS }   from '../../utils/constants';

const EMPTY_FORM = {
  title: '', description: '', assignedUserId: '',
  departmentId: '', dueDate: '', requiresApproval: false,
};

export default function TasksPage({ myTasks }) {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.tasks);
  const { user }          = useSelector(s => s.auth);
  const { list: users }   = useSelector(s => s.users);
  const { list: depts }   = useSelector(s => s.departments);
  const isAdmin = user?.role === 'Admin';

  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('');
  const [page, setPage]             = useState(0);
  const [rowsPerPage]               = useState(15);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [detailTab, setDetailTab]   = useState(0);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [commentText, setComment]   = useState('');
  const [approvalDlg, setApprovalDlg] = useState(false);
  const [approvalDesc, setApprovalDesc] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  useEffect(() => {
    if (myTasks) dispatch(fetchMyTasks());
    else dispatch(fetchTasks());
    if (isAdmin) { dispatch(fetchUsers()); dispatch(fetchDepartments()); }
    else { dispatch(fetchDepartments()); }
  }, [dispatch, myTasks, isAdmin]);

  const filtered = list.filter(t =>
    (!search || t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.assignedUserName || '').toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || t.status === statusFilter)
  );
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openCreate = () => { setForm(EMPTY_FORM); setSaveError(''); setCreateOpen(true); };
  const openEdit   = t  => {
    setForm({
      title: t.title, description: t.description,
      assignedUserId: t.assignedUserId, departmentId: t.departmentId,
      dueDate: t.dueDate?.split('T')[0], requiresApproval: t.requiresApproval,
    });
    setSaveError(''); setEditItem(t);
  };
  const openDetail = async t => {
    setDetailTab(0);
    const res = await dispatch(fetchTask(t.id));
    if (res.payload) setDetailItem(res.payload);
  };
  const closeDetail = () => { setDetailItem(null); setComment(''); setStatusUpdate(''); };

  const handleSave = async () => {
    setSaving(true); setSaveError('');
    const dto = {
      ...form,
      assignedUserId: Number(form.assignedUserId),
      departmentId:   Number(form.departmentId),
      dueDate:        new Date(form.dueDate).toISOString(),
    };
    const res = editItem
      ? await dispatch(updateTask({ id: editItem.id, dto }))
      : await dispatch(createTask(dto));
    if (res.type.endsWith('/rejected')) setSaveError(res.payload || 'Failed to save task.');
    else { setCreateOpen(false); setEditItem(null); }
    setSaving(false);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate || !detailItem) return;
    setSaving(true);
    const res = await dispatch(updateTaskStatus({ id: detailItem.id, dto: { status: statusUpdate } }));
    if (res.type.endsWith('/fulfilled')) setDetailItem(res.payload);
    setSaving(false); setStatusUpdate('');
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !detailItem) return;
    setSaving(true);
    const res = await dispatch(addComment({ id: detailItem.id, dto: { comment: commentText } }));
    if (res.type.endsWith('/fulfilled')) setDetailItem(res.payload);
    setSaving(false); setComment('');
  };

  // Approval request — user sends description
  const handleRequestApproval = async () => {
    if (!approvalDesc.trim() || !detailItem) return;
    setSaving(true);
    const res = await dispatch(requestApproval({ id: detailItem.id, dto: { comment: approvalDesc } }));
    if (res.type.endsWith('/fulfilled')) {
      setDetailItem(res.payload);
      setApprovalDlg(false);
      setApprovalDesc('');
    }
    setSaving(false);
  };

  // Can the current user request approval for this task?
  const canRequestApproval = detailItem &&
    !isAdmin &&
    detailItem.assignedUserId === user?.userId &&
    detailItem.requiresApproval &&
    !detailItem.approvals?.some(a => a.status === 'Pending');

  // ── Task form (create / edit) ──────────────────────────────────────────────
  const TaskForm = (
    <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
      {saveError && <Alert severity="error" sx={{ mb: 0.5 }}>{saveError}</Alert>}
      <TextField
        label="Title" value={form.title}
        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
        fullWidth required
      />
      <TextField
        label="Description" value={form.description}
        onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
        fullWidth multiline rows={3}
      />
      <TextField
        select label="Assignee" value={form.assignedUserId}
        onChange={e => setForm(p => ({ ...p, assignedUserId: e.target.value }))}
        fullWidth required
      >
        {users.filter(u => u.isActive).map(u => (
          <MenuItem key={u.id} value={u.id}>
            {u.name} <Chip label={u.role} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select label="Department" value={form.departmentId}
        onChange={e => setForm(p => ({ ...p, departmentId: e.target.value }))}
        fullWidth required
      >
        {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
      </TextField>
      <TextField
        label="Due Date" type="date" value={form.dueDate}
        onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
        fullWidth required InputLabelProps={{ shrink: true }}
      />
      <TextField
        select label="Requires Approval" value={form.requiresApproval ? 'yes' : 'no'}
        onChange={e => setForm(p => ({ ...p, requiresApproval: e.target.value === 'yes' }))}
        fullWidth
      >
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </TextField>
    </Box>
  );

  if (loading && !list.length) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader
        title={myTasks ? 'My Tasks' : 'All Tasks'}
        subtitle={`${filtered.length} task${filtered.length !== 1 ? 's' : ''}`}
        action={isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Task
          </Button>
        )}
      />

      {/* Filters */}
      <Card sx={{ mb: 2, p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search tasks…" value={search}
          onChange={e => setSearch(e.target.value)} size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select label="Status" value={statusFilter}
          onChange={e => setStatus(e.target.value)} size="small" sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Card>

      {/* Table */}
      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}><EmptyState message="No tasks found." /></TableCell>
              </TableRow>
            ) : paged.map(t => (
              <TableRow key={t.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{t.title}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 700 }}>
                      {getInitials(t.assignedUserName)}
                    </Avatar>
                    <Typography variant="body2">{t.assignedUserName}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{t.departmentName}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2"
                    color={isOverdue(t.dueDate) && t.status !== 'DoneByAdmin' ? 'error.main' : 'text.primary'}>
                    {formatDate(t.dueDate)}
                  </Typography>
                </TableCell>
                <TableCell><StatusChip status={t.status} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => openDetail(t)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={filtered.length} page={page}
          rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)}
          rowsPerPageOptions={[15]}
        />
      </Card>

      {/* ── Create / Edit dialog ─────────────────────────────────────────── */}
      <Dialog
        open={createOpen || !!editItem}
        onClose={() => { setCreateOpen(false); setEditItem(null); }}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ pb: 0, fontFamily: "'Sora',sans-serif", fontWeight: 700 }}>
          {editItem ? 'Edit Task' : 'New Task'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>{TaskForm}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setCreateOpen(false); setEditItem(null); }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave}
            disabled={saving || !form.title || !form.assignedUserId || !form.departmentId || !form.dueDate}
          >
            {saving ? 'Saving…' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Task Detail dialog ───────────────────────────────────────────── */}
      <Dialog open={!!detailItem} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700} fontFamily="'Sora',sans-serif">
                {detailItem?.title}
              </Typography>
              <Box display="flex" gap={1} mt={0.75} flexWrap="wrap">
                <StatusChip status={detailItem?.status} />
                {detailItem?.requiresApproval && (
                  <Chip label="Requires Approval" size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.72rem' }} />
                )}
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ px: 3, mt: 1 }}>
          {['Details', 'Comments', 'History', 'Approvals'].map((l, i) => (
            <Tab key={l} label={l} value={i} />
          ))}
        </Tabs>

        <DialogContent dividers sx={{ minHeight: 300 }}>
          {/* ── DETAILS TAB ── */}
          {detailTab === 0 && detailItem && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Assignee</Typography>
                  <Typography variant="body2" mt={0.5} fontWeight={500}>{detailItem.assignedUserName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Department</Typography>
                  <Typography variant="body2" mt={0.5}>{detailItem.departmentName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Due Date</Typography>
                  <Typography variant="body2" mt={0.5}
                    color={isOverdue(detailItem.dueDate) && detailItem.status !== 'DoneByAdmin' ? 'error.main' : 'text.primary'}>
                    {formatDate(detailItem.dueDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Created By</Typography>
                  <Typography variant="body2" mt={0.5}>{detailItem.createdByName} · {formatDate(detailItem.createdAt)}</Typography>
                </Box>
              </Box>

              {detailItem.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Description</Typography>
                  <Typography variant="body2" mt={0.5} sx={{ whiteSpace: 'pre-wrap' }}>{detailItem.description}</Typography>
                </Box>
              )}

              <Divider />

              {/* Status update */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">Update Status</Typography>
                <Box display="flex" gap={1.5} mt={1} alignItems="center">
                  <TextField
                    select label="New Status" value={statusUpdate}
                    onChange={e => setStatusUpdate(e.target.value)}
                    size="small" sx={{ minWidth: 180 }}
                  >
                    {STATUS_OPTIONS
                      .filter(s => isAdmin || s !== 'DoneByAdmin')
                      .map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <Button variant="contained" size="small" onClick={handleStatusUpdate}
                    disabled={!statusUpdate || saving}>
                    Update
                  </Button>
                </Box>
              </Box>

              {/* ── APPROVAL REQUEST SECTION for assigned user ── */}
              {canRequestApproval && (
                <>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: '#fef9ee', borderRadius: 2, border: '1px solid #fde68a' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircleIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="#92400e">
                        Request Approval
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="#78350f" mb={1.5}>
                      This task requires admin approval. Provide a description of the work completed.
                    </Typography>
                    <Button variant="contained" size="small"
                      sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, boxShadow: 'none' }}
                      onClick={() => setApprovalDlg(true)}>
                      Submit Approval Request
                    </Button>
                  </Box>
                </>
              )}

              {/* Show existing pending approval for this user */}
              {!isAdmin && detailItem.approvals?.some(a => a.status === 'Pending') && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  You have a pending approval request for this task. Awaiting admin review.
                </Alert>
              )}
            </Box>
          )}

          {/* ── COMMENTS TAB ── */}
          {detailTab === 1 && detailItem && (
            <Box>
              {(!detailItem.comments || detailItem.comments.length === 0) && (
                <EmptyState message="No comments yet." />
              )}
              {detailItem.comments?.map(c => (
                <Box key={c.id} sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 700 }}>
                      {getInitials(c.userName)}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="text.primary">{c.userName}</Typography>
                      <Typography variant="caption" color="text.secondary" ml={1}>{formatDateTime(c.createdAt)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ ml: 5, mt: 0.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2">{c.comment}</Typography>
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={1.5} alignItems="flex-end">
                <TextField
                  size="small" fullWidth multiline maxRows={4}
                  placeholder="Write a comment…"
                  value={commentText}
                  onChange={e => setComment(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                />
                <Button variant="contained" size="small" endIcon={<SendIcon />}
                  onClick={handleAddComment} disabled={saving || !commentText.trim()}
                  sx={{ px: 2, whiteSpace: 'nowrap', height: 36 }}>
                  Send
                </Button>
              </Box>
            </Box>
          )}

          {/* ── HISTORY TAB ── */}
          {detailTab === 2 && detailItem && (
            <Box>
              {(!detailItem.history || detailItem.history.length === 0) && (
                <EmptyState message="No history yet." />
              )}
              {detailItem.history?.map(h => (
                <Box key={h.id} display="flex" gap={2} alignItems="flex-start" mb={2}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4f46e5', mt: 0.75, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2">
                      <strong>{h.fieldChanged}</strong>
                      {h.oldValue && h.newValue && (
                        <> changed from <Chip label={h.oldValue} size="small" sx={{ mx: 0.5 }} /> to <Chip label={h.newValue} size="small" sx={{ mx: 0.5 }} color="primary" /></>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.changedByName} · {formatDateTime(h.changedAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* ── APPROVALS TAB ── */}
          {detailTab === 3 && detailItem && (
            <Box>
              {(!detailItem.approvals || detailItem.approvals.length === 0) && (
                <EmptyState message="No approval requests yet." />
              )}
              {detailItem.approvals?.map(a => (
                <Card key={a.id} sx={{ mb: 2, p: 2.5 }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    <ApprovalChip status={a.status} />
                    <Typography variant="caption" color="text.secondary">
                      Requested by <strong>{a.requestedByName}</strong> · {formatDateTime(a.requestedAt)}
                    </Typography>
                  </Box>
                  {a.requestComment && (
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Request Description</Typography>
                      <Typography variant="body2" mt={0.5}>{a.requestComment}</Typography>
                    </Box>
                  )}
                  {a.responseComment && (
                    <Alert severity={a.status === 'Approved' ? 'success' : 'error'} sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight={600}>
                        {a.status === 'Approved' ? 'Approved' : 'Rejected'} by {a.respondedByName}
                      </Typography>
                      <Typography variant="body2">{a.responseComment}</Typography>
                    </Alert>
                  )}
                </Card>
              ))}

              {/* Allow assigned user to request approval from Approvals tab too */}
              {canRequestApproval && (
                <Button variant="outlined" color="warning" startIcon={<CheckCircleIcon />}
                  onClick={() => setApprovalDlg(true)} sx={{ mt: 1 }}>
                  Submit Approval Request
                </Button>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Approval Request dialog ──────────────────────────────────────── */}
      <Dialog open={approvalDlg} onClose={() => setApprovalDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700 }}>
          Submit Approval Request
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Describe the work completed for <strong>{detailItem?.title}</strong>.
            The admin will review and approve or reject it.
          </Typography>
          <TextField
            label="Description of work completed"
            value={approvalDesc}
            onChange={e => setApprovalDesc(e.target.value)}
            fullWidth multiline rows={4} required
            placeholder="e.g. Completed all subtasks, reviewed with team, ready for sign-off…"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setApprovalDlg(false); setApprovalDesc(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleRequestApproval}
            disabled={saving || !approvalDesc.trim()}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {saving ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
